'use client';

import { useState, useRef, useEffect } from 'react';
import {
	MenuExperienceContentSchema,
	type TMenuExperienceContent,
	type TMenuCategory,
	type TMenuItem,
} from '@shared/schemas';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ThemeSelector } from './ThemeSelector';
import { Utensils, Phone, Plus, Trash2, Upload } from 'lucide-react';

type FormValue = Partial<TMenuExperienceContent>;

interface Props {
	value: FormValue;
	onChange: (value: FormValue) => void;
	onValidityChange: (valid: boolean) => void;
}

const CURRENCY_OPTIONS = [
	{ symbol: '$', label: 'USD ($)' },
	{ symbol: '€', label: 'EUR (€)' },
	{ symbol: '£', label: 'GBP (£)' },
	{ symbol: '₦', label: 'NGN (₦)' },
	{ symbol: 'CA$', label: 'CAD ($)' },
	{ symbol: 'A$', label: 'AUD ($)' },
	{ symbol: '¥', label: 'JPY (¥)' },
	{ symbol: 'CHF', label: 'CHF' },
];

const DIETARY_TAGS = [
	{ id: 'vegetarian', label: 'Vegetarian', icon: '🌱' },
	{ id: 'vegan', label: 'Vegan', icon: '🌿' },
	{ id: 'gluten-free', label: 'Gluten-Free', icon: '🌾' },
	{ id: 'spicy', label: 'Spicy', icon: '🌶️' },
	{ id: 'chef-special', label: "Chef's Pick", icon: '⭐' },
	{ id: 'halal', label: 'Halal', icon: '🥩' },
];

export function MenuForm({ value, onChange, onValidityChange }: Props) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

	const initialCategories: TMenuCategory[] =
		value.categories && value.categories.length > 0
			? value.categories
			: [
					{
						id: 'cat-starters',
						name: 'Starters & Appetizers',
						description: 'Light bites and appetizers to start your meal',
						items: [
							{
								id: 'item-1',
								name: 'Bruschetta al Pomodoro',
								description:
									'Grilled country bread, garlic, ripe heirloom tomatoes, basil and EVOO',
								price: '9.50',
								dietary: ['vegetarian', 'vegan'],
								isAvailable: true,
							},
							{
								id: 'item-2',
								name: 'Crispy Calamari',
								description: 'Lightly dusted calamari rings served with lemon garlic aioli',
								price: '14.00',
								dietary: ['chef-special'],
								isAvailable: true,
							},
						],
					},
					{
						id: 'cat-mains',
						name: 'Main Courses',
						description: 'Our signature dishes crafted fresh daily',
						items: [
							{
								id: 'item-3',
								name: 'Truffle Tagliatelle',
								description: 'Handmade pasta, black truffle paste, parmesan cream, wild mushrooms',
								price: '22.50',
								dietary: ['vegetarian', 'chef-special'],
								isAvailable: true,
							},
							{
								id: 'item-4',
								name: 'Wood-Fired Margherita',
								description: 'San Marzano DOP tomatoes, fresh fior di latte, basil, EVOO',
								price: '16.00',
								dietary: ['vegetarian'],
								isAvailable: true,
							},
						],
					},
					{
						id: 'cat-desserts',
						name: 'Desserts & Drinks',
						description: 'Sweet finishes and refreshing beverages',
						items: [
							{
								id: 'item-5',
								name: 'Classic Tiramisu',
								description: 'Espresso-soaked savoiardi, mascarpone cream, dark cocoa dust',
								price: '8.50',
								dietary: ['vegetarian'],
								isAvailable: true,
							},
						],
					},
				];

	const updateFields = (patch: Partial<TMenuExperienceContent>) => {
		const updated = {
			type: 'menu' as const,
			restaurantName: value.restaurantName ?? '',
			currency: value.currency ?? '$',
			categories: value.categories ?? initialCategories,
			accentColor: value.accentColor ?? '#d97706',
			...value,
			...patch,
		};
		onChange(updated);
		onValidityChange(MenuExperienceContentSchema.safeParse(updated).success);
	};

	// Ensure categories exist initially
	useEffect(() => {
		if (!value.categories || value.categories.length === 0) {
			updateFields({ categories: initialCategories });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 1_000_000) {
			alert('Image must be under 1 MB.');
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result === 'string') {
				updateFields({ logoUrl: reader.result });
			}
		};
		reader.readAsDataURL(file);
	};

	const currentCategories = value.categories ?? initialCategories;

	const handleAddCategory = () => {
		const newCategory: TMenuCategory = {
			id: `cat-${Date.now()}`,
			name: 'New Category',
			items: [
				{
					id: `item-${Date.now()}`,
					name: 'New Menu Item',
					description: 'Delicious description of the item',
					price: '10.00',
					dietary: [],
					isAvailable: true,
				},
			],
		};
		const next = [...currentCategories, newCategory];
		updateFields({ categories: next });
		setActiveCategoryIndex(next.length - 1);
	};

	const handleDeleteCategory = (idx: number) => {
		if (currentCategories.length <= 1) {
			alert('Menu must have at least one category.');
			return;
		}
		const next = currentCategories.filter((_, i) => i !== idx);
		updateFields({ categories: next });
		setActiveCategoryIndex(Math.max(0, idx - 1));
	};

	const handleUpdateCategory = (idx: number, patch: Partial<TMenuCategory>) => {
		const next = currentCategories.map((cat, i) => (i === idx ? { ...cat, ...patch } : cat));
		updateFields({ categories: next });
	};

	const handleAddItem = (categoryIdx: number) => {
		const targetCat = currentCategories[categoryIdx];
		if (!targetCat) return;
		const newItem: TMenuItem = {
			id: `item-${Date.now()}`,
			name: 'New Dish or Drink',
			description: 'Brief description of ingredients',
			price: '12.00',
			dietary: [],
			isAvailable: true,
		};
		const next = currentCategories.map((cat, i) =>
			i === categoryIdx ? { ...cat, items: [...cat.items, newItem] } : cat,
		);
		updateFields({ categories: next });
	};

	const handleUpdateItem = (categoryIdx: number, itemIdx: number, patch: Partial<TMenuItem>) => {
		const next = currentCategories.map((cat, i) => {
			if (i !== categoryIdx) return cat;
			const updatedItems = cat.items.map((item, j) =>
				j === itemIdx ? { ...item, ...patch } : item,
			);
			return { ...cat, items: updatedItems };
		});
		updateFields({ categories: next });
	};

	const handleDeleteItem = (categoryIdx: number, itemIdx: number) => {
		const targetCat = currentCategories[categoryIdx];
		if (!targetCat || targetCat.items.length <= 1) {
			alert('Category must have at least one item.');
			return;
		}
		const next = currentCategories.map((cat, i) => {
			if (i !== categoryIdx) return cat;
			return { ...cat, items: cat.items.filter((_, j) => j !== itemIdx) };
		});
		updateFields({ categories: next });
	};

	const toggleDietaryTag = (categoryIdx: number, itemIdx: number, tag: string) => {
		const item = currentCategories[categoryIdx]?.items[itemIdx];
		if (!item) return;
		const existing = item.dietary ?? [];
		const nextDietary = existing.includes(tag)
			? existing.filter((t) => t !== tag)
			: [...existing, tag];
		handleUpdateItem(categoryIdx, itemIdx, { dietary: nextDietary });
	};

	return (
		<div className="space-y-6">
			{/* Restaurant Overview & Branding */}
			<div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xs space-y-5">
				<div className="flex items-center gap-3 border-b border-slate-100 pb-3">
					<div className="flex size-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
						<Utensils className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">Restaurant & Menu Details</h2>
						<p className="text-xs text-slate-500">Name, branding, currency, and dining concept</p>
					</div>
				</div>

				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-1.5 sm:col-span-2">
						<Label htmlFor="restaurantName" className="text-xs font-semibold text-slate-700">
							Restaurant / Brand Name <span className="text-rose-500">*</span>
						</Label>
						<Input
							id="restaurantName"
							value={value.restaurantName ?? ''}
							onChange={(e) => updateFields({ restaurantName: e.target.value })}
							placeholder="e.g. Trattoria da Luigi"
							className="h-10 text-sm font-medium"
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="tagline" className="text-xs font-semibold text-slate-700">
							Tagline / Cuisine
						</Label>
						<Input
							id="tagline"
							value={value.tagline ?? ''}
							onChange={(e) => updateFields({ tagline: e.target.value })}
							placeholder="e.g. Wood-fired Pizza & Artisanal Pastas"
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="currency" className="text-xs font-semibold text-slate-700">
							Currency
						</Label>
						<select
							id="currency"
							value={value.currency ?? '$'}
							onChange={(e) => updateFields({ currency: e.target.value })}
							className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
						>
							{CURRENCY_OPTIONS.map((c) => (
								<option key={c.symbol} value={c.symbol}>
									{c.label}
								</option>
							))}
						</select>
					</div>

					<div className="space-y-1.5 sm:col-span-2">
						<Label htmlFor="description" className="text-xs font-semibold text-slate-700">
							Welcome Note / Concept Story
						</Label>
						<Textarea
							id="description"
							value={value.description ?? ''}
							onChange={(e) => updateFields({ description: e.target.value })}
							placeholder="e.g. Made with love using heritage ingredients imported straight from Italy."
							rows={2}
							className="text-sm resize-none"
						/>
					</div>
				</div>

				{/* Logo Upload & Color Palette */}
				<div className="pt-3 border-t border-slate-100 grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label className="text-xs font-semibold text-slate-700">Logo / Dish Photo</Label>
						<div className="flex items-center gap-3">
							{value.logoUrl ? (
								<div className="relative size-14 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img src={value.logoUrl} alt="Logo preview" className="size-full object-cover" />
									<button
										type="button"
										onClick={() => updateFields({ logoUrl: undefined })}
										className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 transition-opacity hover:opacity-100"
									>
										<Trash2 className="size-4" />
									</button>
								</div>
							) : (
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									className="flex size-14 shrink-0 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 transition hover:border-slate-400 hover:text-slate-600"
								>
									<Upload className="size-5" />
								</button>
							)}
							<div className="text-xs text-slate-500">
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									className="font-medium text-blue-600 hover:underline cursor-pointer"
								>
									Upload logo or photo
								</button>
								<p>PNG, JPG, or WebP under 1 MB</p>
							</div>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/png,image/jpeg,image/webp"
								onChange={handleImageUpload}
								className="hidden"
							/>
						</div>
					</div>

					<div className="pt-2 border-t border-slate-100">
						<ThemeSelector
							selectedTheme={value.theme || 'glass'}
							onThemeChange={(theme) => updateFields({ theme })}
							accentColor={value.accentColor || '#d97706'}
							onAccentColorChange={(accentColor) =>
								updateFields({ accentColor, headerColor: accentColor })
							}
							headerColor={value.headerColor || value.accentColor || '#d97706'}
							onHeaderColorChange={(headerColor) => updateFields({ headerColor })}
						/>
					</div>
				</div>
			</div>

			{/* Contact, Location & Ordering Options */}
			<div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xs space-y-4">
				<div className="flex items-center gap-3 border-b border-slate-100 pb-3">
					<div className="flex size-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
						<Phone className="size-5" />
					</div>
					<div>
						<h2 className="text-base font-bold text-slate-900">Reservations & Contact</h2>
						<p className="text-xs text-slate-500">
							Give guests quick one-tap buttons to call, order, or navigate
						</p>
					</div>
				</div>

				<div className="grid gap-4 sm:grid-cols-3">
					<div className="space-y-1.5">
						<Label htmlFor="menuPhone" className="text-xs font-semibold text-slate-700">
							Phone (Call to Book)
						</Label>
						<Input
							id="menuPhone"
							value={value.phone ?? ''}
							onChange={(e) => updateFields({ phone: e.target.value })}
							placeholder="+1 (555) 000-1122"
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="menuWhatsApp" className="text-xs font-semibold text-slate-700">
							WhatsApp (Order / Chat)
						</Label>
						<Input
							id="menuWhatsApp"
							value={value.whatsApp ?? ''}
							onChange={(e) => updateFields({ whatsApp: e.target.value })}
							placeholder="+1 (555) 000-1122"
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="menuWebsite" className="text-xs font-semibold text-slate-700">
							Website URL
						</Label>
						<Input
							id="menuWebsite"
							type="url"
							value={value.website ?? ''}
							onChange={(e) => updateFields({ website: e.target.value })}
							placeholder="https://example.com"
							className="h-10 text-sm"
						/>
					</div>

					<div className="space-y-1.5 sm:col-span-3">
						<Label htmlFor="menuAddress" className="text-xs font-semibold text-slate-700">
							Restaurant Address
						</Label>
						<Input
							id="menuAddress"
							value={value.address ?? ''}
							onChange={(e) => updateFields({ address: e.target.value })}
							placeholder="123 Culinary Boulevard, Suite 400, New York, NY"
							className="h-10 text-sm"
						/>
					</div>
				</div>
			</div>

			{/* Menu Categories & Items Builder */}
			<div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-xs space-y-6">
				<div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
					<div className="flex items-center gap-3">
						<div className="flex size-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
							<Utensils className="size-5" />
						</div>
						<div>
							<h2 className="text-base font-bold text-slate-900">Menu Sections & Dishes</h2>
							<p className="text-xs text-slate-500">
								Organize your items into categories with prices and dietary labels
							</p>
						</div>
					</div>

					<Button
						type="button"
						size="sm"
						onClick={handleAddCategory}
						className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs cursor-pointer shadow-2xs"
					>
						<Plus className="mr-1.5 size-3.5" />
						Add Category
					</Button>
				</div>

				{/* Category Pill Tabs */}
				<div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 pb-3">
					{currentCategories.map((cat, idx) => {
						const active = idx === activeCategoryIndex;
						return (
							<button
								key={cat.id || idx}
								type="button"
								onClick={() => setActiveCategoryIndex(idx)}
								className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
									active
										? 'bg-amber-500 text-white shadow-xs'
										: 'bg-slate-100 text-slate-700 hover:bg-slate-200'
								}`}
							>
								<span>{cat.name || `Section ${idx + 1}`}</span>
								<span
									className={`text-[10px] rounded-full px-1.5 py-0.2 ${
										active ? 'bg-amber-700/60 text-white' : 'bg-slate-200 text-slate-600'
									}`}
								>
									{cat.items.length}
								</span>
							</button>
						);
					})}
				</div>

				{/* Active Category Editor */}
				{currentCategories[activeCategoryIndex] && (
					<div className="space-y-5 rounded-xl border border-slate-200/90 bg-slate-50/50 p-4">
						<div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
							<div className="flex-1 min-w-[200px] space-y-1">
								<Label className="text-xs font-bold text-slate-700">Category Name</Label>
								<Input
									value={currentCategories[activeCategoryIndex].name}
									onChange={(e) =>
										handleUpdateCategory(activeCategoryIndex, { name: e.target.value })
									}
									placeholder="Category name"
									className="h-9 text-sm font-semibold bg-white"
								/>
							</div>

							<div className="flex items-center gap-2 pt-4">
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => handleDeleteCategory(activeCategoryIndex)}
									className="text-rose-600 hover:bg-rose-50 border-rose-200 text-xs cursor-pointer"
								>
									<Trash2 className="mr-1 size-3.5" />
									Delete Section
								</Button>
							</div>
						</div>

						{/* Items in Category */}
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
									Items in this section ({currentCategories[activeCategoryIndex].items.length})
								</span>
								<Button
									type="button"
									size="sm"
									variant="outline"
									onClick={() => handleAddItem(activeCategoryIndex)}
									className="text-xs border-slate-300 hover:bg-white text-slate-700 cursor-pointer"
								>
									<Plus className="mr-1 size-3.5" />
									Add Item
								</Button>
							</div>

							{currentCategories[activeCategoryIndex].items.map((item, itemIdx) => (
								<div
									key={item.id || itemIdx}
									className="rounded-xl border border-slate-200 bg-white p-4 shadow-2xs space-y-3"
								>
									<div className="flex items-start justify-between gap-3">
										<div className="flex-1 grid gap-3 sm:grid-cols-[1fr_120px]">
											<div className="space-y-1">
												<Label className="text-[11px] font-semibold text-slate-600">
													Item Name <span className="text-rose-500">*</span>
												</Label>
												<Input
													value={item.name}
													onChange={(e) =>
														handleUpdateItem(activeCategoryIndex, itemIdx, {
															name: e.target.value,
														})
													}
													placeholder="e.g. Margherita Pizza"
													className="h-9 text-sm font-medium"
												/>
											</div>

											<div className="space-y-1">
												<Label className="text-[11px] font-semibold text-slate-600">
													Price ({value.currency ?? '$'})
												</Label>
												<Input
													value={item.price}
													onChange={(e) =>
														handleUpdateItem(activeCategoryIndex, itemIdx, {
															price: e.target.value,
														})
													}
													placeholder="14.50"
													className="h-9 text-sm font-semibold"
												/>
											</div>
										</div>

										<button
											type="button"
											onClick={() => handleDeleteItem(activeCategoryIndex, itemIdx)}
											className="mt-6 p-1.5 text-slate-400 hover:text-rose-600 transition rounded-lg hover:bg-rose-50 cursor-pointer"
											title="Remove item"
										>
											<Trash2 className="size-4" />
										</button>
									</div>

									<div className="space-y-1">
										<Label className="text-[11px] font-semibold text-slate-600">
											Description & Ingredients
										</Label>
										<Input
											value={item.description ?? ''}
											onChange={(e) =>
												handleUpdateItem(activeCategoryIndex, itemIdx, {
													description: e.target.value,
												})
											}
											placeholder="e.g. San Marzano tomatoes, fresh mozzarella, basil, EVOO"
											className="h-8 text-xs"
										/>
									</div>

									{/* Dietary Labels Badges */}
									<div className="space-y-1.5 pt-1">
										<Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
											Dietary & Specialty Tags
										</Label>
										<div className="flex flex-wrap items-center gap-1.5">
											{DIETARY_TAGS.map((tag) => {
												const active = (item.dietary ?? []).includes(tag.id);
												return (
													<button
														key={tag.id}
														type="button"
														onClick={() => toggleDietaryTag(activeCategoryIndex, itemIdx, tag.id)}
														className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition cursor-pointer flex items-center gap-1 ${
															active
																? 'bg-amber-100 text-amber-900 border-amber-300 font-semibold'
																: 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
														}`}
													>
														<span>{tag.icon}</span>
														<span>{tag.label}</span>
													</button>
												);
											})}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
